/**
 *  @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon, Message } from 'semantic-ui-react';
import {
    map, omit, get, reduce, flatten, uniqBy, property, sortBy, isEmpty,
} from 'lodash';
import { format } from 'date-fns';
import uuid from 'uuid';

import { Modal, Button } from '../../../components';
import MembershipForm from './form/MembershipForm';
import { prepareFloatForServer } from '../../../lib/numberFormatter';
import { GET_MEMBERSHIPS_QUERY, GET_ALL_SERVICES_QUERY } from '../containers/queries';

const ADD_MEMBERSHIP_MUTATION = gql`
    mutation createMembership($input: CreateMembershipInput!, $lang: String!) {
        createMembership(input: $input) {
            membership {
                id
                client {
                    id
                    firstName
                    lastName
                }
                author {
                    id
                    firstName
                    lastName
                }
                createdAt
                serialNumber
                service {
                    id
                    name(lang: $lang)
                    duration
                    price
                }
                totalUses
                quantity
                price
                pricePerUse
            }
        }
    }
`;

const intlMessages = defineMessages({
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
    nameExistingsError: {
        id: 'pages.memberships.add.nameExistingsError',
        defaultMessage: 'Membership with same serial number already exists',
    },
});

function computeAllServices(viewer) {
    const mastersServiceGroups = uniqBy(
        flatten(map(get(viewer, 'salon.masters.nodes', []), i => i.serviceGroups)),
        property('id'),
    );
    const mastersServices = mastersServiceGroups.length
        ? reduce(mastersServiceGroups, (res, { services }) => [...res, ...services], [])
        : [];

    const cabinetServiceGroups = uniqBy(
        flatten(map(get(viewer, 'salon.cabinets', []), i => i.serviceGroups)),
        property('id'),
    );
    const cabinetsServices = cabinetServiceGroups.length
        ? reduce(cabinetServiceGroups, (res, { services }) => [...res, ...services], [])
        : [];

    // A service can belong to several groups (and to both a master's and a
    // cabinet's group), so de-dupe by id — otherwise the Service dropdown
    // renders duplicate React keys.
    return uniqBy([...mastersServices, ...cabinetsServices], property('id'));
}

function getNextSerialNumber(memberships) {
    if (isEmpty(memberships)) return 1;
    const last = sortBy(memberships, i => i.serialNumber)[memberships.length - 1];
    return (last && parseInt(last.serialNumber) + 1) || 1;
}

function CreateMembershipDialog({ trigger, open, onClose, memberships, intl }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const userId = useSelector(({ user }) => user.get('id'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );

    const { data: servicesData, loading: servicesLoading } = useQuery(GET_ALL_SERVICES_QUERY, {
        variables: { salon, lang: currentLanguage },
        fetchPolicy: 'cache-and-network',
        skip: !open,
    });

    const allServices = !servicesLoading && servicesData
        ? computeAllServices(servicesData.viewer)
        : [];

    const methods = useForm({
        defaultValues: {
            serialNumber: getNextSerialNumber(memberships),
            createdAt: format(new Date(), 'yyyy-MM-dd HH:mm'),
            quantity: '',
            price: '',
        },
    });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    useEffect(() => {
        if (open) {
            reset({
                serialNumber: getNextSerialNumber(memberships),
                createdAt: format(new Date(), 'yyyy-MM-dd HH:mm'),
                quantity: '',
                price: '',
            });
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const [createMembership] = useMutation(ADD_MEMBERSHIP_MUTATION, {
        update: (store, { data: { createMembership: result } }) => {
            try {
                const data = store.readQuery({
                    query: GET_MEMBERSHIPS_QUERY,
                    variables: { salon, lang: currentLanguage },
                });
                store.writeQuery({
                    query: GET_MEMBERSHIPS_QUERY,
                    variables: { salon, lang: currentLanguage },
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            salon: {
                                ...data.viewer.salon,
                                memberships: [
                                    ...data.viewer.salon.memberships,
                                    result.membership,
                                ],
                            },
                        },
                    },
                });
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onModalClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async formData => {
        const submitData = omit(formData, [
            'notes', 'duration', 'createdAt', 'totalUses', 'quantity',
            'price', 'pricePerUse', 'serialNumber',
        ]);
        const price = prepareFloatForServer(formData.price);
        const quantity = parseInt(formData.quantity);
        const serialNumber = parseInt(formData.serialNumber);
        const pricePerUse = prepareFloatForServer(price / quantity).toFixed(2);
        const createdAt = format(new Date(formData.createdAt), 'yyyy-MM-dd HH:mm');

        const selectedService = allServices.find(s => s.id === formData.service);

        try {
            await createMembership({
                variables: {
                    input: {
                        salon,
                        ...submitData,
                        author: userId,
                        price,
                        quantity,
                        createdAt,
                        pricePerUse,
                        serialNumber,
                    },
                    lang: currentLanguage,
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    createMembership: {
                        __typename: 'CreateMembershipPayload',
                        membership: {
                            __typename: 'Membership',
                            id: uuid.v4(),
                            client: { __typename: 'Client', id: formData.client, firstName: '', lastName: '' },
                            author: { __typename: 'Administrator', id: userId, firstName: '', lastName: '' },
                            serialNumber: formData.serialNumber,
                            service: {
                                __typename: 'Service',
                                id: formData.service,
                                name: selectedService ? selectedService.name : '',
                                duration: selectedService ? selectedService.duration : 0,
                                price: selectedService ? selectedService.price : '0',
                            },
                            quantity,
                            price,
                            pricePerUse,
                            totalUses: 0,
                            createdAt,
                        },
                    },
                },
            });
            onModalClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'AlreadyExists') {
                    setError('root', { message: intl.formatMessage(intlMessages.nameExistingsError) });
                    return;
                }
                setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onModalClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.memberships.form.newMembership"
                        defaultMessage="New membership"
                    />
                </Modal.Header>
                <Modal.Content>
                    <MembershipForm
                        services={allServices}
                        createMode
                        onCloseDialog={onClose}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.memberships.form.save"
                            defaultMessage="Create"
                        />
                    </Button>
                    <Button onClick={onModalClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.memberships.form.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(CreateMembershipDialog);
